import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import {
  Drawer,
  DrawerForm,
  DrawerFormActions,
  EmptyState,
  ErrorState,
  FormField,
  LoadingSkeleton,
  PageHeader,
  PageViewerControls,
  Select,
  StatusChip,
  TextInput,
  Textarea,
  toast,
  KpiHeroBand,
  KpiBandSkeleton,
  StatusDonutMini,
  BrandedEmptyState,
} from '../../components/index.js';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { PlacematDraftPanel } from './PlacematDraftPanel.jsx';
import { ProvenanceBadge, normalizedProvenance } from '../../components/ProvenanceBadge.jsx';
import { useUrlPagination } from '../../lib/usePagination.js';
import {
  buildCloseoutPackagePath,
  buildFinalProjectReportPath,
} from '../briefings/reportEntryLinks.js';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import './knowledge.css';
import './knowledge.redesign.css';

// Map each knowledge-search result `type` (artifact | decision | lesson |
// closeout) to an executive-traffic-light tone + display label. Tone names bind
// straight to the StatusDonutMini / bento spine tokens — no raw colors.
const KNOWLEDGE_TYPE_META = {
  artifact: { label: 'Artifacts', tone: 'blue' },
  lesson: { label: 'Lessons learned', tone: 'sage' },
  decision: { label: 'Decisions', tone: 'amber' },
  closeout: { label: 'Closeout records', tone: 'slate' },
};

function knowledgeTypeMeta(type) {
  const key = String(type || '').toLowerCase();
  return KNOWLEDGE_TYPE_META[key] || { label: type || 'Other', tone: 'slate' };
}

// Convert authoritative, page-independent API facets into the ordered KPI and
// chart model used by the library.
function buildKnowledgeTypeCounts(facets = []) {
  const counts = new Map(facets.map((facet) => [String(facet.type), Number(facet.total) || 0]));
  const order = ['artifact', 'lesson', 'decision', 'closeout'];
  const known = order
    .filter((key) => counts.has(key))
    .map((key) => ({ key, count: counts.get(key), ...knowledgeTypeMeta(key) }));
  const extras = [...counts.keys()]
    .filter((key) => !order.includes(key))
    .map((key) => ({ key, count: counts.get(key), ...knowledgeTypeMeta(key) }));
  return [...known, ...extras];
}

const ARTIFACT_TYPE_OPTIONS = [
  { value: 'placemat', label: 'Placemat' },
  { value: 'informationPaper', label: 'Information paper' },
  { value: 'closeoutPackage', label: 'Closeout package' },
  { value: 'decisionMemo', label: 'Decision memo' },
  { value: 'lessonsLearned', label: 'Lessons learned' },
  { value: 'other', label: 'Other' },
];

const CLASSIFICATION_OPTIONS = [
  { value: 'Illustrative Data', label: 'Illustrative Data' },
  { value: 'Unclassified', label: 'Unclassified' },
  { value: 'CUI', label: 'CUI' },
  { value: 'PHI', label: 'PHI' },
  { value: 'PII', label: 'PII' },
];

const BOOLEAN_OPTIONS = [
  { value: 'false', label: 'No' },
  { value: 'true', label: 'Yes' },
];

const DISPOSITION_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'pending', label: 'Pending' },
];

const DECISION_TYPE_OPTIONS = [
  { value: 'gate', label: 'Gate decision' },
  { value: 'scope', label: 'Scope decision' },
  { value: 'resource', label: 'Resource decision' },
  { value: 'closeout', label: 'Closeout decision' },
];

function parseDelimitedList(value = '') {
  return [
    ...new Set(
      String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

function parseBoolean(value) {
  return String(value) === 'true';
}

// Artifact types arrive as camelCase tokens ("informationPaper",
// "closeoutPackage", "intakeForm"). The `.eyebrow` treatment uppercases them,
// so a raw token renders run-together as "INFORMATIONPAPER". Split camelCase
// and delimiter boundaries into words — mirroring the repo's `humanizeFieldKey`
// convention in ProjectPages — so the eyebrow reads "INFORMATION PAPER".
function humanizeArtifactType(value) {
  const spaced = String(value ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return spaced.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ArtifactCard({ artifact }) {
  const provenance = normalizedProvenance(artifact, 'artifact');
  const isPlacemat =
    (artifact.artifactType || artifact.documentType || artifact.type) === 'placemat';
  return (
    <article className="knowledge-card">
      <div className="row spread" style={{ gap: 12 }}>
        <div>
          <div className="eyebrow">
            {humanizeArtifactType(artifact.artifactType || artifact.documentType || artifact.type)}
          </div>
          <h4 style={{ margin: '4px 0 0' }}>{artifact.title || artifact.name || artifact.id}</h4>
        </div>
        <StatusChip status={artifact.status || artifact.reviewState || 'draft'} />
      </div>
      <p className="muted">{artifact.summary || 'No artifact summary provided.'}</p>
      <div className="knowledge-card-footer">
        <ProvenanceBadge value={artifact} kind="artifact" />
        {provenance.sourceMode === 'generated_output' ? (
          <span className="knowledge-provenance-note">Generated output</span>
        ) : null}
        <span className="chip outline sm">
          {artifact.classification || artifact.classificationLabel || 'Illustrative Data'}
        </span>
        {artifact.officialRecordFlag ? <span className="chip blue sm">Official record</span> : null}
        <Link to={`/knowledge/artifacts/${artifact.id}`}>Open detail</Link>
        {isPlacemat ? (
          <Link className="chip outline sm" to={`/knowledge/artifacts/${artifact.id}`}>
            Draft placemat with AI
          </Link>
        ) : null}
      </div>
    </article>
  );
}

function DecisionRow({ decision }) {
  return (
    <article className="knowledge-row">
      <div className="row spread" style={{ gap: 12 }}>
        <strong>{decision.decision || decision.title || decision.id}</strong>
        <span className="chip outline sm">{decision.decisionType || 'decision'}</span>
      </div>
      <p className="muted">{decision.rationale || 'No rationale recorded.'}</p>
    </article>
  );
}

function LessonRow({ lesson, onApprove, approving = false }) {
  const tags = Array.isArray(lesson?.applicabilityTags) ? lesson.applicabilityTags : [];
  const evidenceArtifactIds = Array.isArray(lesson?.evidenceArtifactIds)
    ? lesson.evidenceArtifactIds
    : [];

  return (
    <article className="knowledge-row">
      <div className="row spread" style={{ gap: 12 }}>
        <strong>{lesson.theme || 'Lesson learned'}</strong>
        <div className="knowledge-lesson-actions">
          <span className={`chip ${lesson.approvedForReuse ? 'sage' : 'amber'} sm`}>
            {lesson.approvedForReuse ? 'Approved for reuse' : 'Pending reuse approval'}
          </span>
          {!lesson.approvedForReuse && onApprove ? (
            <button
              type="button"
              className="btn outline sm"
              onClick={() => onApprove(lesson)}
              disabled={approving}
            >
              Approve for reuse
            </button>
          ) : null}
        </div>
      </div>
      <p>{lesson.lesson}</p>
      <p className="muted">{lesson.recommendation}</p>
      {tags.length || evidenceArtifactIds.length ? (
        <div className="knowledge-lesson-meta">
          {tags.map((tag) => (
            <span key={tag} className="chip outline sm">
              {tag}
            </span>
          ))}
          {evidenceArtifactIds.map((artifactId) => (
            <Link
              key={artifactId}
              className="chip blue sm"
              to={`/knowledge/artifacts/${encodeURIComponent(artifactId)}`}
              title={`Open evidence artifact ${artifactId}`}
            >
              Evidence: {artifactId}
            </Link>
          ))}
        </div>
      ) : null}
    </article>
  );
}

function formatUsageLabel(value) {
  switch (String(value || '').toLowerCase()) {
    case 'decision':
      return 'Decision reference';
    case 'lesson':
      return 'Lesson evidence';
    case 'record':
      return 'Official record';
    default:
      return value || 'Linked evidence';
  }
}

function formatActivityLabel(value) {
  return String(value || 'audit event')
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (token) => token.toUpperCase());
}

function VersionRow({ version }) {
  const provenance = normalizedProvenance(version, 'artifact');
  return (
    <article className="knowledge-row">
      <div className="row spread" style={{ gap: 12 }}>
        <strong>{version.artifactTitle || version.artifactId || version.id}</strong>
        <span className="chip outline sm">{version.version || 'Unversioned'}</span>
      </div>
      <p className="muted">{version.changeSummary || 'No version change summary recorded.'}</p>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <ProvenanceBadge value={version} kind="artifact" />
        {provenance.sourceMode === 'generated_output' ? (
          <span className="knowledge-provenance-note">Generated output</span>
        ) : null}
        {!provenance.storageVerified ? (
          <span className="knowledge-provenance-note is-caution">Storage reference unverified</span>
        ) : null}
      </div>
      {version.route ? <Link to={version.route}>Open artifact</Link> : null}
    </article>
  );
}

function RecordsMetadataRow({ record }) {
  return (
    <article className="knowledge-row">
      <div className="row spread" style={{ gap: 12 }}>
        <strong>{record.artifactTitle || record.entityId || record.id}</strong>
        <span className={`chip ${record.masked ? 'amber' : 'outline'} sm`}>
          {record.masked ? 'Restricted' : record.dispositionStatus || 'Recorded'}
        </span>
      </div>
      <p className="muted">
        Series {record.recordSeries || 'Unassigned'} · Retention {record.retentionPeriod || 'TBD'}
        {' · '}Disposition {record.dispositionStatus || 'Unknown'}
      </p>
      {record.maskReason ? <p className="muted small">{record.maskReason}</p> : null}
      {record.route ? <Link to={record.route}>Open artifact</Link> : null}
    </article>
  );
}

function ActivityRow({ event }) {
  return (
    <article className="knowledge-row">
      <div className="row spread" style={{ gap: 12 }}>
        <strong>{event.summary || 'Audit event recorded.'}</strong>
        <span className="chip outline sm">{formatActivityLabel(event.action)}</span>
      </div>
      <p className="muted small">
        {(event.occurredAt || '').slice(0, 10) || 'Unknown date'}
        {' · '}
        {event.targetType || 'project'}
        {event.targetId ? ` ${event.targetId}` : ''}
      </p>
    </article>
  );
}

function SourceEvidenceRow({ evidence }) {
  return (
    <article className="knowledge-row">
      <div className="row spread" style={{ gap: 12 }}>
        <strong>{evidence.artifactTitle || evidence.artifactId}</strong>
        {evidence.classification ? (
          <span className="chip outline sm">{evidence.classification}</span>
        ) : null}
      </div>
      <div className="knowledge-row-meta">
        {(evidence.linkedFrom || []).map((usage) => (
          <span key={usage} className="chip blue sm">
            {formatUsageLabel(usage)}
          </span>
        ))}
      </div>
      {evidence.route ? <Link to={evidence.route}>Open artifact</Link> : null}
    </article>
  );
}

function ReadinessList({ title, items }) {
  return (
    <div className="knowledge-readiness-list">
      <strong>{title}</strong>
      {items.length === 0 ? (
        <p className="muted small">None</p>
      ) : (
        <ul>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AddArtifactDrawer({ open, onClose, projectId, onCreated }) {
  const mutation = useMutation();
  const [draft, setDraft] = useState({
    title: '',
    artifactType: 'placemat',
    classification: 'Illustrative Data',
    officialRecord: 'false',
    summary: '',
  });

  function update(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function submit() {
    try {
      const response = await mutation.run(() =>
        api.post('/artifacts', {
          projectId,
          title: draft.title.trim(),
          artifactType: draft.artifactType,
          documentType: draft.artifactType,
          classification: draft.classification,
          classificationLabel: draft.classification,
          officialRecordFlag: parseBoolean(draft.officialRecord),
          summary: draft.summary.trim(),
        }),
      );
      onCreated?.(response.data);
      setDraft({
        title: '',
        artifactType: 'placemat',
        classification: 'Illustrative Data',
        officialRecord: 'false',
        summary: '',
      });
      onClose?.();
      toast.success({
        title: 'Artifact created',
        body: 'The project knowledge room now includes the new artifact.',
      });
    } catch (error) {
      toast.error({ title: 'Artifact creation failed', body: error.message });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Add artifact" width={520}>
      <DrawerForm onSubmit={submit} submitting={mutation.running}>
        <FormField label="Artifact title" htmlFor="artifact-title">
          <TextInput
            id="artifact-title"
            value={draft.title}
            onChange={(value) => update('title', value)}
            placeholder="e.g. Closeout source memo"
            autoFocus
          />
        </FormField>
        <FormField label="Artifact type" htmlFor="artifact-type">
          <Select
            id="artifact-type"
            value={draft.artifactType}
            onChange={(value) => update('artifactType', value)}
            options={ARTIFACT_TYPE_OPTIONS}
          />
        </FormField>
        <FormField label="Classification" htmlFor="artifact-classification">
          <Select
            id="artifact-classification"
            value={draft.classification}
            onChange={(value) => update('classification', value)}
            options={CLASSIFICATION_OPTIONS}
          />
        </FormField>
        <FormField label="Official record" htmlFor="artifact-official-record">
          <Select
            id="artifact-official-record"
            value={draft.officialRecord}
            onChange={(value) => update('officialRecord', value)}
            options={BOOLEAN_OPTIONS}
          />
        </FormField>
        <FormField label="Summary" htmlFor="artifact-summary">
          <Textarea
            id="artifact-summary"
            value={draft.summary}
            onChange={(value) => update('summary', value)}
            placeholder="Evidence package prepared for project closure."
            rows={4}
          />
        </FormField>
        <DrawerFormActions
          onCancel={onClose}
          submitLabel="Create artifact"
          submitting={mutation.running}
        />
      </DrawerForm>
    </Drawer>
  );
}

function ArtifactMetadataDrawer({ open, onClose, detail, onSaved }) {
  const mutation = useMutation();
  const artifact = detail?.artifact || {};
  const recordsMetadata = detail?.recordsMetadata || {};
  const [draft, setDraft] = useState({
    classification: artifact.classification || artifact.classificationLabel || 'Illustrative Data',
    officialRecord: String(Boolean(artifact.officialRecordFlag)),
    recordSeries: recordsMetadata.recordSeries || '',
    retentionPeriod: recordsMetadata.retentionPeriod || '',
    dispositionStatus: recordsMetadata.dispositionStatus || 'active',
  });

  function update(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function submit() {
    try {
      const response = await mutation.run(() =>
        api.patch(`/artifacts/${artifact.id}`, {
          classification: draft.classification,
          classificationLabel: draft.classification,
          officialRecordFlag: parseBoolean(draft.officialRecord),
          recordSeries: draft.recordSeries.trim(),
          retentionPeriod: draft.retentionPeriod.trim(),
          dispositionStatus: draft.dispositionStatus,
        }),
      );
      onSaved?.(response.data);
      onClose?.();
      toast.success({
        title: 'Artifact metadata saved',
        body: 'Classification and records posture were updated.',
      });
    } catch (error) {
      toast.error({ title: 'Artifact update failed', body: error.message });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Edit metadata" width={520}>
      <DrawerForm onSubmit={submit} submitting={mutation.running}>
        <FormField label="Classification" htmlFor="artifact-detail-classification">
          <Select
            id="artifact-detail-classification"
            value={draft.classification}
            onChange={(value) => update('classification', value)}
            options={CLASSIFICATION_OPTIONS}
          />
        </FormField>
        <FormField label="Official record" htmlFor="artifact-detail-official-record">
          <Select
            id="artifact-detail-official-record"
            value={draft.officialRecord}
            onChange={(value) => update('officialRecord', value)}
            options={BOOLEAN_OPTIONS}
          />
        </FormField>
        <FormField label="Record series" htmlFor="artifact-detail-record-series">
          <TextInput
            id="artifact-detail-record-series"
            value={draft.recordSeries}
            onChange={(value) => update('recordSeries', value)}
            placeholder="OWHA-PLCMT"
          />
        </FormField>
        <FormField label="Retention period" htmlFor="artifact-detail-retention-period">
          <TextInput
            id="artifact-detail-retention-period"
            value={draft.retentionPeriod}
            onChange={(value) => update('retentionPeriod', value)}
            placeholder="6 years"
          />
        </FormField>
        <FormField label="Disposition status" htmlFor="artifact-detail-disposition-status">
          <Select
            id="artifact-detail-disposition-status"
            value={draft.dispositionStatus}
            onChange={(value) => update('dispositionStatus', value)}
            options={DISPOSITION_OPTIONS}
          />
        </FormField>
        <DrawerFormActions
          onCancel={onClose}
          submitLabel="Save metadata"
          submitting={mutation.running}
        />
      </DrawerForm>
    </Drawer>
  );
}

function ArtifactVersionDrawer({ open, onClose, artifactId, onSaved }) {
  const mutation = useMutation();
  const [draft, setDraft] = useState({
    version: '',
    storageRef: '',
    changeSummary: '',
  });

  function update(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function submit() {
    try {
      const response = await mutation.run(() =>
        api.post(`/artifacts/${artifactId}/versions`, {
          version: draft.version.trim(),
          storageRef: draft.storageRef.trim(),
          changeSummary: draft.changeSummary.trim(),
        }),
      );
      onSaved?.(response.data);
      setDraft({ version: '', storageRef: '', changeSummary: '' });
      onClose?.();
      toast.success({
        title: 'Artifact version saved',
        body: 'The new artifact version is now available in the history.',
      });
    } catch (error) {
      toast.error({ title: 'Version save failed', body: error.message });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Add version" width={520}>
      <DrawerForm onSubmit={submit} submitting={mutation.running}>
        <FormField label="Version label" htmlFor="artifact-version-label">
          <TextInput
            id="artifact-version-label"
            value={draft.version}
            onChange={(value) => update('version', value)}
            placeholder="v2.0"
            autoFocus
          />
        </FormField>
        <FormField label="Storage reference" htmlFor="artifact-storage-ref">
          <TextInput
            id="artifact-storage-ref"
            value={draft.storageRef}
            onChange={(value) => update('storageRef', value)}
            placeholder="s3://synthetic/placemat-v2.pptx"
          />
        </FormField>
        <FormField label="Change summary" htmlFor="artifact-change-summary">
          <Textarea
            id="artifact-change-summary"
            value={draft.changeSummary}
            onChange={(value) => update('changeSummary', value)}
            placeholder="Director review updates"
            rows={4}
          />
        </FormField>
        <DrawerFormActions
          onCancel={onClose}
          submitLabel="Save version"
          submitting={mutation.running}
        />
      </DrawerForm>
    </Drawer>
  );
}

function DecisionDrawer({ open, onClose, projectId, onCreated }) {
  const mutation = useMutation();
  const [draft, setDraft] = useState({
    decisionType: 'gate',
    decision: '',
    rationale: '',
  });

  function update(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function submit() {
    try {
      const response = await mutation.run(() =>
        api.post('/decisions', {
          scopeType: 'project',
          scopeId: projectId,
          projectId,
          decisionType: draft.decisionType,
          decision: draft.decision.trim(),
          rationale: draft.rationale.trim(),
        }),
      );
      onCreated?.(response.data);
      setDraft({ decisionType: 'gate', decision: '', rationale: '' });
      onClose?.();
      toast.success({
        title: 'Decision recorded',
        body: 'The decision log now includes the new rationale.',
      });
    } catch (error) {
      toast.error({ title: 'Decision save failed', body: error.message });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Add decision" width={520}>
      <DrawerForm onSubmit={submit} submitting={mutation.running}>
        <FormField label="Decision type" htmlFor="decision-type">
          <Select
            id="decision-type"
            value={draft.decisionType}
            onChange={(value) => update('decisionType', value)}
            options={DECISION_TYPE_OPTIONS}
          />
        </FormField>
        <FormField label="Decision" htmlFor="decision-value">
          <TextInput
            id="decision-value"
            value={draft.decision}
            onChange={(value) => update('decision', value)}
            placeholder="Approve sustainment pilot"
            autoFocus
          />
        </FormField>
        <FormField label="Rationale" htmlFor="decision-rationale">
          <Textarea
            id="decision-rationale"
            value={draft.rationale}
            onChange={(value) => update('rationale', value)}
            placeholder="Director agreed to proceed after rehearsal review."
            rows={4}
          />
        </FormField>
        <DrawerFormActions
          onCancel={onClose}
          submitLabel="Create decision"
          submitting={mutation.running}
        />
      </DrawerForm>
    </Drawer>
  );
}

function LessonDrawer({ open, onClose, projectId, onCreated, availableArtifacts = [] }) {
  const mutation = useMutation();
  const [draft, setDraft] = useState({
    theme: '',
    lesson: '',
    recommendation: '',
    tags: '',
    evidenceArtifactIds: [],
  });

  function update(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function toggleEvidenceArtifact(artifactId, checked) {
    setDraft((current) => ({
      ...current,
      evidenceArtifactIds: checked
        ? [...new Set([...(current.evidenceArtifactIds || []), artifactId])]
        : (current.evidenceArtifactIds || []).filter((value) => value !== artifactId),
    }));
  }

  async function submit() {
    try {
      const response = await mutation.run(() =>
        api.post('/lessons-learned', {
          projectId,
          theme: draft.theme.trim(),
          lesson: draft.lesson.trim(),
          recommendation: draft.recommendation.trim(),
          applicabilityTags: parseDelimitedList(draft.tags),
          evidenceArtifactIds: draft.evidenceArtifactIds,
        }),
      );
      onCreated?.(response.data);
      setDraft({ theme: '', lesson: '', recommendation: '', tags: '', evidenceArtifactIds: [] });
      onClose?.();
      toast.success({
        title: 'Lesson captured',
        body: 'The lesson is now available in the project knowledge room.',
      });
    } catch (error) {
      toast.error({ title: 'Lesson save failed', body: error.message });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Add lesson" width={520}>
      <DrawerForm onSubmit={submit} submitting={mutation.running}>
        <FormField label="Theme" htmlFor="lesson-theme">
          <TextInput
            id="lesson-theme"
            value={draft.theme}
            onChange={(value) => update('theme', value)}
            placeholder="Capacity"
            autoFocus
          />
        </FormField>
        <FormField label="Lesson" htmlFor="lesson-text">
          <Textarea
            id="lesson-text"
            value={draft.lesson}
            onChange={(value) => update('lesson', value)}
            placeholder="Lock surge staffing before the Director rehearsal."
            rows={4}
          />
        </FormField>
        <FormField label="Recommendation" htmlFor="lesson-recommendation">
          <Textarea
            id="lesson-recommendation"
            value={draft.recommendation}
            onChange={(value) => update('recommendation', value)}
            placeholder="Assign backup support two weeks earlier."
            rows={4}
          />
        </FormField>
        <FormField label="Applicability tags" htmlFor="lesson-tags">
          <TextInput
            id="lesson-tags"
            value={draft.tags}
            onChange={(value) => update('tags', value)}
            placeholder="capacity, closeout, sustainment"
          />
        </FormField>
        <FormField label="Evidence artifacts" htmlFor="lesson-evidence-artifacts">
          <div id="lesson-evidence-artifacts" className="knowledge-choice-grid">
            {availableArtifacts.length === 0 ? (
              <p className="muted small">
                Create or link artifacts first to cite them as evidence.
              </p>
            ) : (
              availableArtifacts.map((artifact) => {
                const checked = (draft.evidenceArtifactIds || []).includes(artifact.id);
                return (
                  <label key={artifact.id} className="knowledge-choice-row">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) =>
                        toggleEvidenceArtifact(artifact.id, event.target.checked)
                      }
                    />
                    <span>{artifact.title || artifact.id}</span>
                  </label>
                );
              })
            )}
          </div>
        </FormField>
        <DrawerFormActions
          onCancel={onClose}
          submitLabel="Create lesson"
          submitting={mutation.running}
        />
      </DrawerForm>
    </Drawer>
  );
}

function knowledgeRangeLabel(rows, total, page, expanded) {
  if (!expanded) return `Showing ${rows.length} of ${total}`;
  const from = rows.length ? Number(page?.offset || 0) + 1 : 0;
  const to = Number(page?.offset || 0) + rows.length;
  return `Showing ${from}\u2013${to} of ${Number(page?.total ?? total)}`;
}

function KnowledgeFamilyPanel({
  projectId,
  apiFamily,
  familyLabel,
  title,
  previewRows,
  preview,
  totalTestId,
  emptyTitle,
  emptyDescription,
  renderRow,
  actions = null,
}) {
  const [expanded, setExpanded] = useState(false);
  const [offset, setOffset] = useState(0);
  const pageResult = useApi(`/projects/${projectId}/knowledge-room/${apiFamily}`, {
    query: { limit: 25, offset },
    skip: !projectId || !expanded,
  });
  const rows = expanded ? pageResult.data?.data || [] : previewRows;
  const total = Number(preview?.total ?? previewRows.length);
  const page = pageResult.data?.page;

  return (
    <div className="knowledge-panel">
      <div className="row spread" style={{ gap: 12 }}>
        <h4>{title}</h4>
        <div className="knowledge-panel-actions">
          {actions}
          <span className="chip outline sm" data-testid={totalTestId}>
            {knowledgeRangeLabel(rows, total, page, expanded)}
          </span>
        </div>
      </div>
      {expanded && pageResult.loading ? (
        <LoadingSkeleton rows={3} />
      ) : expanded && pageResult.error ? (
        <ErrorState error={pageResult.error} />
      ) : rows.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <div className="knowledge-stack">{rows.map(renderRow)}</div>
      )}
      {preview?.truncated || expanded ? (
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => {
            setOffset(0);
            setExpanded((value) => !value);
          }}
        >
          {expanded ? `Show ${familyLabel} preview` : `View All ${familyLabel}`}
        </button>
      ) : null}
      {expanded && page ? (
        <PageViewerControls
          page={page}
          pageSize={25}
          pageSizeOptions={[25]}
          label={familyLabel}
          onOffsetChange={setOffset}
        />
      ) : null}
    </div>
  );
}

export function ProjectKnowledgeRoomTab({ projectId }) {
  const knowledge = useApi(`/projects/${projectId}/knowledge-room`, { skip: !projectId });
  const approveLessonMutation = useMutation();
  const [addArtifactOpen, setAddArtifactOpen] = useState(false);
  const [addDecisionOpen, setAddDecisionOpen] = useState(false);
  const [addLessonOpen, setAddLessonOpen] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [activityOffset, setActivityOffset] = useState(0);
  const [showAllSourceEvidence, setShowAllSourceEvidence] = useState(false);
  const [sourceEvidenceOffset, setSourceEvidenceOffset] = useState(0);
  const activityPage = useApi(`/projects/${projectId}/knowledge-room/activity`, {
    query: { limit: 25, offset: activityOffset },
    skip: !projectId || !showAllActivity,
  });
  const sourceEvidencePage = useApi(`/projects/${projectId}/knowledge-room/source-evidence`, {
    query: { limit: 25, offset: sourceEvidenceOffset },
    skip: !projectId || !showAllSourceEvidence,
  });

  if (knowledge.loading) return <LoadingSkeleton rows={6} />;
  if (knowledge.error) return <ErrorState error={knowledge.error} />;

  const data = knowledge.data?.data;
  const artifacts = data?.artifacts || [];
  const artifactVersions = data?.artifactVersions || [];
  const decisions = data?.decisions || [];
  const lessonsLearned = data?.lessonsLearned || [];
  const recordsMetadata = data?.recordsMetadata || [];
  const activity = data?.activity || [];
  const sourceEvidencePreview = data?.sourceEvidence || [];
  const readiness = data?.closeoutReadiness;
  const previews = data?.previews || {};
  const previewTotal = (family, rows) => Number(previews[family]?.total ?? rows.length);
  const displayedActivity = showAllActivity ? activityPage.data?.data || [] : activity;
  const activityTotal = previewTotal('activity', activity);
  const activityPageMeta = activityPage.data?.page;
  const displayedSourceEvidence = showAllSourceEvidence
    ? sourceEvidencePage.data?.data || []
    : sourceEvidencePreview;
  const sourceEvidenceTotal = previewTotal('sourceEvidence', sourceEvidencePreview);
  const sourceEvidencePageMeta = sourceEvidencePage.data?.page;

  async function approveLesson(lesson) {
    try {
      const response = await approveLessonMutation.run(() =>
        api.patch(`/lessons-learned/${lesson.id}`, {
          approvedForReuse: true,
        }),
      );
      const nextLesson = response.data;
      knowledge.setData((previous) => ({
        ...(previous || {}),
        data: {
          ...(previous?.data || {}),
          lessonsLearned: (previous?.data?.lessonsLearned || []).map((item) =>
            item.id === nextLesson.id ? nextLesson : item,
          ),
        },
      }));
      toast.success({
        title: 'Lesson approved',
        body: 'The lesson is now reusable across OWHA delivery work.',
      });
      knowledge.refresh?.();
    } catch (error) {
      toast.error({ title: 'Lesson approval failed', body: error.message });
    }
  }

  return (
    <>
      <div className="profile-tab knowledge-room-tab">
        <section className="knowledge-hero">
          <div>
            <div className="eyebrow">Project Knowledge Room</div>
            <h3>Project Knowledge Room</h3>
            <p className="muted">
              Artifacts, decisions, reusable lessons, and closeout posture for{' '}
              {data?.project?.name || projectId}.
            </p>
          </div>
          <div className="knowledge-hero-actions">
            <button
              type="button"
              className="btn primary sm"
              onClick={() => setAddArtifactOpen(true)}
            >
              Add artifact
            </button>
            <Link className="btn outline sm" to={`/projects/${projectId}/closeout`}>
              Open closeout builder
            </Link>
            <Link className="btn ghost sm" to="/knowledge">
              Search library
            </Link>
          </div>
        </section>

        <section className="knowledge-grid">
          <KnowledgeFamilyPanel
            projectId={projectId}
            apiFamily="artifacts"
            familyLabel="artifacts"
            title="Artifacts"
            previewRows={artifacts}
            preview={previews.artifacts}
            totalTestId="knowledge-artifacts-total"
            emptyTitle="No artifacts yet"
            emptyDescription="Artifacts linked to this project will appear here."
            renderRow={(artifact) => <ArtifactCard key={artifact.id} artifact={artifact} />}
          />

          <KnowledgeFamilyPanel
            projectId={projectId}
            apiFamily="decisions"
            familyLabel="decisions"
            title="Decision log"
            previewRows={decisions}
            preview={previews.decisions}
            totalTestId="knowledge-decisions-total"
            emptyTitle="No decisions recorded"
            emptyDescription="Auditable decision rationale will appear here."
            actions={
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => setAddDecisionOpen(true)}
              >
                Add decision
              </button>
            }
            renderRow={(decision) => <DecisionRow key={decision.id} decision={decision} />}
          />

          <KnowledgeFamilyPanel
            projectId={projectId}
            apiFamily="lessons-learned"
            familyLabel="lessons learned"
            title="Lessons learned"
            previewRows={lessonsLearned}
            preview={previews.lessonsLearned}
            totalTestId="knowledge-lessons-total"
            emptyTitle="No lessons captured"
            emptyDescription="Lessons and reuse recommendations will appear here."
            actions={
              <button type="button" className="btn ghost sm" onClick={() => setAddLessonOpen(true)}>
                Add lesson
              </button>
            }
            renderRow={(lesson) => (
              <LessonRow
                key={lesson.id}
                lesson={lesson}
                onApprove={approveLesson}
                approving={approveLessonMutation.running}
              />
            )}
          />

          <div className="knowledge-panel">
            <div className="row spread" style={{ gap: 12 }}>
              <h4>Closeout readiness</h4>
              <StatusChip status={readiness?.canApprove ? 'approved' : 'blocked'}>
                {readiness?.canApprove ? 'Ready for approval' : 'Approval blocked'}
              </StatusChip>
            </div>
            <div className="knowledge-readiness-grid">
              <ReadinessList
                title="Missing artifacts"
                items={(readiness?.summary?.missingArtifactTypes || []).map((type) =>
                  humanizeEnum(type),
                )}
              />
              <ReadinessList
                title="Missing record posture"
                items={readiness?.summary?.missingOfficialRecordMetadata || []}
              />
            </div>
            <div className="knowledge-readiness-metrics">
              <span className="chip outline sm">
                {readiness?.summary?.openActionCount || 0} open actions
              </span>
              <span className="chip outline sm">
                {readiness?.summary?.openRiskCount || 0} open risks
              </span>
              <span className="chip outline sm">
                {readiness?.summary?.lessonCount || 0} lessons captured
              </span>
            </div>
          </div>

          <KnowledgeFamilyPanel
            projectId={projectId}
            apiFamily="artifact-versions"
            familyLabel="artifact versions"
            title="Version history"
            previewRows={artifactVersions}
            preview={previews.artifactVersions}
            totalTestId="knowledge-versions-total"
            emptyTitle="No artifact versions"
            emptyDescription="Version history will appear as project artifacts evolve."
            renderRow={(version) => <VersionRow key={version.id} version={version} />}
          />

          <KnowledgeFamilyPanel
            projectId={projectId}
            apiFamily="records-metadata"
            familyLabel="records metadata"
            title="Records metadata"
            previewRows={recordsMetadata}
            preview={previews.recordsMetadata}
            totalTestId="knowledge-records-total"
            emptyTitle="No records metadata"
            emptyDescription="Official records posture will appear here once artifacts are governed."
            renderRow={(record) => (
              <RecordsMetadataRow key={record.id || record.entityId} record={record} />
            )}
          />

          <div className="knowledge-panel">
            <div className="row spread" style={{ gap: 12 }}>
              <h4>Recent activity</h4>
              <span className="chip outline sm" data-testid="knowledge-activity-total">
                {knowledgeRangeLabel(
                  displayedActivity,
                  activityTotal,
                  activityPageMeta,
                  showAllActivity,
                )}
              </span>
            </div>
            {showAllActivity && activityPage.loading ? (
              <LoadingSkeleton rows={3} />
            ) : showAllActivity && activityPage.error ? (
              <ErrorState error={activityPage.error} />
            ) : displayedActivity.length === 0 ? (
              <EmptyState
                title="No linked activity"
                description="Project audit and change activity will appear here."
              />
            ) : (
              <div className="knowledge-stack">
                {displayedActivity.map((event) => (
                  <ActivityRow key={event.id} event={event} />
                ))}
              </div>
            )}
            {previews.activity?.truncated || showAllActivity ? (
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => {
                  setActivityOffset(0);
                  setShowAllActivity((value) => !value);
                }}
              >
                {showAllActivity ? 'Show preview' : 'View All activity'}
              </button>
            ) : null}
            {showAllActivity && activityPageMeta ? (
              <PageViewerControls
                page={activityPageMeta}
                pageSize={25}
                pageSizeOptions={[25]}
                label="activity"
                onOffsetChange={setActivityOffset}
              />
            ) : null}
          </div>

          <div className="knowledge-panel">
            <div className="row spread" style={{ gap: 12 }}>
              <h4>Source evidence</h4>
              <span className="chip outline sm" data-testid="knowledge-source-evidence-total">
                {knowledgeRangeLabel(
                  displayedSourceEvidence,
                  sourceEvidenceTotal,
                  sourceEvidencePageMeta,
                  showAllSourceEvidence,
                )}
              </span>
            </div>
            {showAllSourceEvidence && sourceEvidencePage.loading ? (
              <LoadingSkeleton rows={3} />
            ) : showAllSourceEvidence && sourceEvidencePage.error ? (
              <ErrorState error={sourceEvidencePage.error} />
            ) : displayedSourceEvidence.length === 0 ? (
              <EmptyState
                title="No source evidence linked"
                description="Linked evidence artifacts will appear here as decisions and lessons cite them."
              />
            ) : (
              <div className="knowledge-stack">
                {displayedSourceEvidence.map((evidence) => (
                  <SourceEvidenceRow key={evidence.artifactId || evidence.id} evidence={evidence} />
                ))}
              </div>
            )}
            {previews.sourceEvidence?.truncated || showAllSourceEvidence ? (
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => {
                  setSourceEvidenceOffset(0);
                  setShowAllSourceEvidence((value) => !value);
                }}
              >
                {showAllSourceEvidence
                  ? 'Show source evidence preview'
                  : 'View All source evidence'}
              </button>
            ) : null}
            {showAllSourceEvidence && sourceEvidencePageMeta ? (
              <PageViewerControls
                page={sourceEvidencePageMeta}
                pageSize={25}
                pageSizeOptions={[25]}
                label="source evidence"
                onOffsetChange={setSourceEvidenceOffset}
              />
            ) : null}
          </div>
        </section>
      </div>
      <AddArtifactDrawer
        open={addArtifactOpen}
        onClose={() => setAddArtifactOpen(false)}
        projectId={projectId}
        onCreated={(artifact) => {
          knowledge.setData((previous) => ({
            ...(previous || {}),
            data: {
              ...(previous?.data || {}),
              artifacts: [artifact, ...(previous?.data?.artifacts || [])],
            },
          }));
          knowledge.refresh?.();
        }}
      />
      <DecisionDrawer
        open={addDecisionOpen}
        onClose={() => setAddDecisionOpen(false)}
        projectId={projectId}
        onCreated={(decision) => {
          knowledge.setData((previous) => ({
            ...(previous || {}),
            data: {
              ...(previous?.data || {}),
              decisions: [decision, ...(previous?.data?.decisions || [])],
            },
          }));
          knowledge.refresh?.();
        }}
      />
      <LessonDrawer
        open={addLessonOpen}
        onClose={() => setAddLessonOpen(false)}
        projectId={projectId}
        availableArtifacts={artifacts}
        onCreated={(lesson) => {
          knowledge.setData((previous) => ({
            ...(previous || {}),
            data: {
              ...(previous?.data || {}),
              lessonsLearned: [lesson, ...(previous?.data?.lessonsLearned || [])],
            },
          }));
          knowledge.refresh?.();
        }}
      />
    </>
  );
}

export function KnowledgeLibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const pager = useUrlPagination(searchParams, setSearchParams, { defaultPageSize: 9 });
  usePageMetadata({
    title: 'Knowledge Library',
    subtitle: 'Search artifacts, lessons learned, closeout packages, and decision rationale.',
  });
  const results = useApi('/knowledge', {
    query: { q: query, limit: pager.pageSize, offset: pager.offset },
  });
  const items = results.data?.data || [];
  const facets = results.data?.facets || { grandTotal: 0, types: [] };
  const total = results.data?.page?.total ?? facets.grandTotal;
  const typeCounts = buildKnowledgeTypeCounts(facets.types);
  const kpiItems = [
    {
      id: 'total',
      label: 'Indexed records',
      value: facets.grandTotal,
      icon: '📚',
      footer: query ? `Matching “${query}”` : 'Across the OWHA portfolio',
    },
    ...typeCounts.slice(0, 3).map((entry) => ({
      id: entry.key,
      label: entry.label,
      value: entry.count,
      status: entry.tone,
      statusLabel: entry.label,
    })),
  ];
  const donutSegments = typeCounts.map((entry) => ({
    label: entry.label,
    count: entry.count,
    tone: entry.tone,
  }));
  const ready = !results.loading && !results.error;
  const hasResults = total > 0;

  return (
    <>
      <PageHeader
        eyebrow="Knowledge"
        title="Knowledge Library"
        subtitle="Cross-project retrieval for artifacts, lessons learned, and closeout records."
      />
      <section className="knowledge-library-shell">
        <label className="knowledge-search-field">
          <span>Search knowledge</span>
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              if (pager.offset) pager.setOffset(0);
            }}
            placeholder="Search placemats, lessons, rationale"
          />
        </label>

        {results.loading ? <KpiBandSkeleton count={4} /> : null}
        {results.error ? <ErrorState error={results.error} /> : null}

        {ready && hasResults ? (
          <>
            <div className="knowledge-library-overview">
              <KpiHeroBand
                eyebrow="Library composition"
                asOf={query ? undefined : results.loadedAt}
                context={query ? 'Filtered' : undefined}
                items={kpiItems}
                columns={Math.min(4, kpiItems.length)}
              />
              {donutSegments.length ? (
                <aside className="knowledge-mix-card" aria-label="Knowledge type mix">
                  <div className="knowledge-mix-card__head">
                    <span className="eyebrow">By category</span>
                    <span className="knowledge-mix-card__title">Record type mix</span>
                  </div>
                  <StatusDonutMini
                    segments={donutSegments}
                    size={104}
                    totalLabel="records"
                    ariaLabel="Knowledge records by type"
                  />
                </aside>
              ) : null}
            </div>

            <div className="knowledge-results-meta">
              <span className="eyebrow">Results</span>
              <span className="chip outline sm">{total} total</span>
            </div>

            <div className="knowledge-bento">
              {items.map((item) => {
                const meta = knowledgeTypeMeta(item.type);
                return (
                  <article
                    key={`${item.type}-${item.id}`}
                    className={`knowledge-bento-card tone-${meta.tone}`}
                  >
                    <div className="knowledge-bento-card__head">
                      <div>
                        <div className="eyebrow">{meta.label}</div>
                        <h4 className="knowledge-bento-card__title">{item.title || item.id}</h4>
                      </div>
                      <span className={`chip ${meta.tone} sm`} title={item.type}>
                        {humanizeArtifactType(item.type)}
                      </span>
                    </div>
                    <p className="knowledge-bento-card__summary">
                      {item.summary || 'No summary recorded.'}
                    </p>
                    <div className="knowledge-bento-card__footer">
                      <span className="muted small">{item.id}</span>
                      {item.route ? <Link to={item.route}>Open</Link> : null}
                    </div>
                  </article>
                );
              })}
            </div>
            <PageViewerControls
              page={results.data?.page}
              label="knowledge results"
              pageSize={pager.pageSize}
              onOffsetChange={pager.setOffset}
              onPageSizeChange={pager.setPageSize}
            />
          </>
        ) : null}

        {ready && !hasResults ? (
          <BrandedEmptyState
            icon="🔍"
            eyebrow="Knowledge Library"
            title="No knowledge results"
            description={
              query
                ? 'No artifacts, lessons, decisions, or closeout records match this term. Try a broader search or open a project knowledge room.'
                : 'Search across artifacts, lessons learned, decision rationale, and closeout records, or open a project knowledge room.'
            }
            tone="slate"
          />
        ) : null}
      </section>
    </>
  );
}

export function LessonsLearnedLibraryPage() {
  usePageMetadata({
    title: 'Lessons Learned Library',
    subtitle: 'Reusable lessons and recommendations across OWHA delivery.',
  });
  const lessons = useApi('/knowledge/lessons-learned');
  const approveLessonMutation = useMutation();
  const items = lessons.data?.data || [];

  async function approveLesson(lesson) {
    try {
      const response = await approveLessonMutation.run(() =>
        api.patch(`/lessons-learned/${lesson.id}`, {
          approvedForReuse: true,
        }),
      );
      const nextLesson = response.data;
      lessons.setData((previous) => ({
        ...(previous || {}),
        data: {
          ...(previous?.data || {}),
          data: (previous?.data?.data || []).map((item) =>
            item.id === nextLesson.id ? nextLesson : item,
          ),
        },
      }));
      toast.success({
        title: 'Lesson approved',
        body: 'The lesson is now reusable across OWHA delivery work.',
      });
    } catch (error) {
      toast.error({ title: 'Lesson approval failed', body: error.message });
    }
  }

  const approvedCount = items.filter((lesson) => lesson?.approvedForReuse).length;
  const pendingCount = items.length - approvedCount;
  const donutSegments = [
    { label: 'Approved for reuse', count: approvedCount, tone: 'sage' },
    { label: 'Pending approval', count: pendingCount, tone: 'amber' },
  ];
  const kpiItems = [
    {
      id: 'total',
      label: 'Lessons captured',
      value: items.length,
      icon: '🧭',
      footer: 'Across OWHA delivery',
    },
    {
      id: 'approved',
      label: 'Approved for reuse',
      value: approvedCount,
      status: 'sage',
      statusLabel: 'Reusable',
    },
    {
      id: 'pending',
      label: 'Pending approval',
      value: pendingCount,
      status: pendingCount > 0 ? 'amber' : 'sage',
      statusLabel: pendingCount > 0 ? 'Needs review' : 'Clear',
    },
  ];

  const ready = !lessons.loading && !lessons.error;
  const hasLessons = items.length > 0;

  return (
    <>
      <PageHeader
        eyebrow="Knowledge"
        title="Lessons Learned Library"
        subtitle="Approved and draft lessons learned available for future reuse."
      />
      {lessons.loading ? <KpiBandSkeleton count={3} /> : null}
      {lessons.error ? <ErrorState error={lessons.error} /> : null}
      {ready && hasLessons ? (
        <>
          <div className="knowledge-library-overview">
            <KpiHeroBand
              eyebrow="Reuse readiness"
              asOf={lessons.loadedAt}
              items={kpiItems}
              columns={3}
            />
            <aside className="knowledge-mix-card" aria-label="Lesson reuse mix">
              <div className="knowledge-mix-card__head">
                <span className="eyebrow">Reuse posture</span>
                <span className="knowledge-mix-card__title">Approval status</span>
              </div>
              <StatusDonutMini
                segments={donutSegments}
                size={104}
                totalLabel="lessons"
                ariaLabel="Lessons by reuse-approval status"
              />
            </aside>
          </div>
          <section className="knowledge-stack">
            {items.map((lesson) => (
              <LessonRow
                key={lesson.id}
                lesson={lesson}
                onApprove={approveLesson}
                approving={approveLessonMutation.running}
              />
            ))}
          </section>
        </>
      ) : null}
      {ready && !hasLessons ? (
        <BrandedEmptyState
          icon="🧭"
          eyebrow="Lessons Learned"
          title="No lessons learned"
          description="Lessons will appear here after closeout or retrospectives, ready to reuse across OWHA delivery."
          tone="slate"
        />
      ) : null}
    </>
  );
}

export function ArtifactDetailPage() {
  const { id } = useParams();
  const detail = useApi(`/knowledge/artifacts/${id}`, { skip: !id });
  const exportMut = useMutation();
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  usePageMetadata({
    title: detail.data?.data?.artifact?.title || 'Artifact detail',
    subtitle: 'Version history, record posture, linked decisions, and audit summary.',
  });

  if (detail.loading) return <LoadingSkeleton rows={6} />;
  if (detail.error) return <ErrorState error={detail.error} />;

  const data = detail.data?.data;
  const accessPolicy = data?.accessPolicy || {};
  const exportPolicy = data?.exportPolicy || { canExport: true, blockedReasons: [] };
  const isPlacemat =
    data?.isPlacemat ??
    (data?.artifact?.documentType || data?.artifact?.artifactType || data?.artifact?.type) ===
      'placemat';
  const placematProjectLive = data?.placematProjectLive ?? data?.linkedProject?.live ?? false;
  const artifactProvenance = normalizedProvenance(data?.artifact, 'artifact');

  function handlePlacematChange({ placematDraft: nextDraft, artifact: nextArtifact } = {}) {
    detail.setData((previous) => ({
      ...(previous || {}),
      data: {
        ...(previous?.data || {}),
        ...(nextDraft ? { placematDraft: nextDraft } : {}),
        ...(nextArtifact ? { artifact: nextArtifact } : {}),
      },
    }));
  }

  async function exportArtifact() {
    try {
      const response = await exportMut.run(() =>
        api.post(`/artifacts/${id}/export`, { format: 'pdf' }),
      );
      detail.setData((previous) => ({
        ...(previous || {}),
        data: {
          ...(previous?.data || {}),
          artifact: response.data.artifact,
        },
      }));
      toast.success({
        title: 'Artifact exported',
        body: `${response.data.exportRecord.fileName} is ready in the governed export history.`,
      });
    } catch (error) {
      toast.error({ title: 'Artifact export blocked', body: error.message });
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Knowledge"
        title={data?.artifact?.title || 'Artifact detail'}
        subtitle="Version history, record posture, linked decisions, and audit summary."
      />
      <section className="knowledge-toolbar">
        <button
          type="button"
          className="btn outline sm"
          onClick={() => setMetadataOpen(true)}
          disabled={accessPolicy.masked}
        >
          Edit metadata
        </button>
        <button
          type="button"
          className="btn primary sm"
          onClick={() => setVersionOpen(true)}
          disabled={accessPolicy.masked}
        >
          Add version
        </button>
        <button
          type="button"
          className="btn outline sm"
          onClick={exportArtifact}
          disabled={!exportPolicy.canExport || exportMut.running}
        >
          Export PDF
        </button>
      </section>
      {isPlacemat ? (
        <PlacematDraftPanel
          artifactId={id}
          artifact={data?.artifact}
          placematDraft={data?.placematDraft}
          projectLive={placematProjectLive}
          onChange={handlePlacematChange}
          onApproved={() => detail.refresh?.()}
        />
      ) : null}
      <section className="knowledge-grid">
        <div className="knowledge-panel">
          <div className="row spread" style={{ gap: 12 }}>
            <h4>Artifact posture</h4>
            <StatusChip status={data?.artifact?.status || data?.artifact?.reviewState || 'draft'} />
          </div>
          <p className="muted">{data?.artifact?.summary || 'No artifact summary recorded.'}</p>
          <div className="knowledge-readiness-metrics">
            <ProvenanceBadge value={data?.artifact} kind="artifact" />
            {artifactProvenance.sourceMode === 'generated_output' ? (
              <span className="knowledge-provenance-note">Generated output</span>
            ) : null}
            <span className="chip outline sm">
              {humanizeArtifactType(
                data?.artifact?.artifactType ||
                  data?.artifact?.documentType ||
                  data?.artifact?.type,
              )}
            </span>
            <span className="chip outline sm">
              {data?.artifact?.classification ||
                data?.artifact?.classificationLabel ||
                'Illustrative Data'}
            </span>
            {data?.artifact?.officialRecordFlag ? (
              <span className="chip blue sm">Official record</span>
            ) : null}
          </div>
        </div>
        <div className="knowledge-panel">
          <h4>Version history</h4>
          <div className="knowledge-stack">
            {(data?.versions || []).map((version) => (
              <article key={version.id} className="knowledge-row">
                <div className="row spread" style={{ gap: 12 }}>
                  <strong>{version.version}</strong>
                  <span className="chip outline sm">{version.id}</span>
                </div>
                <p className="muted">{version.changeSummary || 'No change summary recorded.'}</p>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <ProvenanceBadge value={version} kind="artifact" />
                  {normalizedProvenance(version, 'artifact').sourceMode === 'generated_output' ? (
                    <span className="knowledge-provenance-note">Generated output</span>
                  ) : null}
                  {!normalizedProvenance(version, 'artifact').storageVerified ? (
                    <span className="knowledge-provenance-note is-caution">
                      Storage reference unverified
                    </span>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </div>
        <div className="knowledge-panel">
          <h4>Linked project</h4>
          {data?.linkedProject ? (
            <div className="knowledge-stack">
              <div className="knowledge-row">
                <Link
                  to={
                    data.linkedProject.route || `/projects/${data.linkedProject.id}?tab=knowledge`
                  }
                >
                  {data.linkedProject.name || data.linkedProject.id}
                </Link>
                <p className="muted">
                  Lifecycle stage {data.linkedProject.lifecycleStage || 'unavailable'}
                </p>
              </div>
            </div>
          ) : (
            <EmptyState
              title="No linked project"
              description="This artifact is not currently linked to a project workspace."
            />
          )}
        </div>
        <div className="knowledge-panel">
          <h4>Record posture</h4>
          {data?.recordsMetadata ? (
            <div className="knowledge-stack">
              <div className="knowledge-row">
                <strong>{data.recordsMetadata.recordSeries}</strong>
                <p className="muted">
                  {data.recordsMetadata.retentionPeriod} · {data.recordsMetadata.dispositionStatus}
                </p>
              </div>
              {data.recordsMetadata.maskReason ? (
                <p className="muted small">{data.recordsMetadata.maskReason}</p>
              ) : null}
            </div>
          ) : (
            <EmptyState
              title="No records metadata"
              description="This artifact is not yet fully classified for closeout."
            />
          )}
        </div>
        <div className="knowledge-panel">
          <h4>Access and export posture</h4>
          <p className="muted">
            {accessPolicy.message || 'Artifact access posture is available for your current role.'}
          </p>
          <div className="knowledge-readiness-metrics">
            {(accessPolicy.sensitivityFlags || []).map((flag) => (
              <span key={flag} className="chip outline sm">
                {flag.toUpperCase()}
              </span>
            ))}
            {accessPolicy.masked ? <span className="chip amber sm">Masked</span> : null}
            {exportPolicy.requiresApproval ? (
              <span className="chip blue sm">Approval required</span>
            ) : null}
          </div>
          <p className="muted small">
            {exportPolicy.canExport
              ? 'Export is allowed for your current role.'
              : exportPolicy.blockedReasons?.[0] ||
                'Artifact export is blocked for your current role.'}
          </p>
        </div>
        <div className="knowledge-panel">
          <h4>Audit summary</h4>
          {(data?.auditSummary || []).length === 0 ? (
            <EmptyState
              title="No audit events"
              description="Artifact audit events will appear here."
            />
          ) : (
            <div className="knowledge-stack">
              {data.auditSummary.map((event) => (
                <article key={event.id} className="knowledge-row">
                  <strong>{event.action}</strong>
                  <p className="muted">{event.occurredAt || 'No timestamp recorded.'}</p>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
      <ArtifactMetadataDrawer
        open={metadataOpen}
        onClose={() => setMetadataOpen(false)}
        detail={data}
        onSaved={(response) => {
          detail.setData((previous) => ({
            ...(previous || {}),
            data: {
              ...(previous?.data || {}),
              artifact: response.artifact,
              recordsMetadata: response.recordsMetadata,
            },
          }));
        }}
      />
      <ArtifactVersionDrawer
        open={versionOpen}
        onClose={() => setVersionOpen(false)}
        artifactId={id}
        onSaved={(response) => {
          detail.setData((previous) => ({
            ...(previous || {}),
            data: {
              ...(previous?.data || {}),
              artifact: response.artifact,
              versions: [response.version, ...(previous?.data?.versions || [])],
            },
          }));
        }}
      />
    </>
  );
}

export function CloseoutPackagePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const closeout = useApi(`/projects/${id}/closeout-package`, { skip: !id });
  const project = useApi(`/projects/${id}`, { skip: !id });
  const saveMut = useMutation();
  const approveMut = useMutation();
  const archiveMut = useMutation();
  usePageMetadata({
    title: 'Closeout Package Builder',
    subtitle: 'Required records, lessons learned, and approval posture before closure.',
  });

  const readiness = closeout.data?.data?.readiness;
  const closeoutStatus = closeout.data?.data?.status || 'draft';
  const projectStatus = project.data?.data?.status || 'active';
  const projectArchived =
    projectStatus === 'archived' || project.data?.data?.lifecycleStage === 'archived';
  // SM-08: routing and approval are separate, confirmed transitions —
  // 'route-for-approval' lands in pending_approval, never self-approves.
  const [pendingCloseoutAction, setPendingCloseoutAction] = useState(null); // 'route' | 'approve' | 'archive'

  function mergeCloseout(response) {
    closeout.setData((previous) => ({
      ...(previous || {}),
      data: {
        ...(previous?.data || {}),
        ...(response?.data || {}),
      },
    }));
  }

  async function saveCloseoutDraft() {
    try {
      // Saving preserves the package's current status — it never submits or
      // approves (SM-08: "Save" saves a draft).
      const response = await saveMut.run(() => api.post(`/projects/${id}/closeout-package`, {}));
      mergeCloseout(response);
      toast.success({
        title: 'Closeout draft saved',
        body: `Package saved — status remains ${response?.data?.status || closeoutStatus}.`,
      });
    } catch (error) {
      toast.error({ title: 'Closeout save failed', body: error.message });
    }
  }

  async function routeForApproval() {
    try {
      const response = await approveMut.run(() =>
        api.post(`/projects/${id}/closeout-package`, {
          action: 'route-for-approval',
        }),
      );
      mergeCloseout(response);
      toast.success({
        title: 'Routed for approval',
        body: 'The closeout package is now pending an explicit approval decision.',
      });
    } catch (error) {
      toast.error({ title: 'Routing failed', body: error.message });
    }
  }

  async function approveCloseoutPackage() {
    try {
      const response = await approveMut.run(() =>
        api.post(`/projects/${id}/closeout-package`, {
          action: 'approve',
        }),
      );
      mergeCloseout(response);
      toast.success({
        title: 'Closeout package approved',
        body: 'The closeout package is now approved for project archival.',
      });
    } catch (error) {
      toast.error({ title: 'Approval failed', body: error.message });
    }
  }

  async function archiveProject() {
    try {
      const response = await archiveMut.run(() =>
        api.patch(`/projects/${id}`, {
          status: 'archived',
          lifecycleStage: 'archived',
        }),
      );
      project.setData((previous) => ({
        ...(previous || {}),
        data: {
          ...(previous?.data || {}),
          ...(response?.data || {}),
        },
      }));
      toast.success({
        title: 'Project archived',
        body: 'The project is now archived with the approved closeout package on record.',
      });
      navigate(`/projects/${id}?tab=archive`);
    } catch (error) {
      toast.error({ title: 'Archive failed', body: error.message });
    }
  }

  if (closeout.loading) return <LoadingSkeleton rows={6} />;
  if (closeout.error) return <ErrorState error={closeout.error} />;

  return (
    <>
      <PageHeader
        eyebrow="Knowledge"
        title="Closeout Package Builder"
        subtitle="Required records, lessons learned, and approval posture before closure."
      />
      <section className="row" style={{ gap: 12, marginBottom: 16 }}>
        <Link
          className="btn outline sm"
          to={buildCloseoutPackagePath({ scopeType: 'project', scopeId: id })}
        >
          Draft closeout package
        </Link>
        <Link
          className="btn ghost sm tactile-press"
          to={buildFinalProjectReportPath({ scopeType: 'project', scopeId: id })}
        >
          Draft final project report
        </Link>
      </section>
      <section className="knowledge-grid">
        <div className="knowledge-panel" data-testid="closeout-readiness-card">
          <div className="row spread" style={{ gap: 12 }}>
            <h4>Approval posture</h4>
            <StatusChip status={readiness?.canApprove ? 'approved' : 'blocked'}>
              {readiness?.canApprove ? 'Ready for approval' : 'Approval blocked'}
            </StatusChip>
          </div>
          <p className="muted">
            Save the package once the required records are aligned. Approval remains blocked until
            the readiness gate is clear.
          </p>
          <div className="knowledge-readiness-metrics">
            <span className="chip outline sm">Closeout status {closeoutStatus}</span>
            <span className={`chip ${projectArchived ? 'sage' : 'outline'} sm`}>
              Project lifecycle{' '}
              {projectArchived ? 'archived' : project.data?.data?.lifecycleStage || 'active'}
            </span>
          </div>
          <div className="knowledge-readiness-grid">
            <ReadinessList
              title="Missing artifacts"
              items={(readiness?.summary?.missingArtifactTypes || []).map((type) =>
                humanizeEnum(type),
              )}
            />
            <ReadinessList
              title="Missing record posture"
              items={readiness?.summary?.missingOfficialRecordMetadata || []}
            />
          </div>
          <div className="knowledge-readiness-metrics">
            <span className="chip outline sm">
              {readiness?.summary?.openActionCount || 0} open actions
            </span>
            <span className="chip outline sm">
              {readiness?.summary?.openRiskCount || 0} open risks
            </span>
            <span className="chip outline sm">
              {readiness?.summary?.lessonCount || 0} lessons captured
            </span>
          </div>
          <button
            type="button"
            className="btn primary"
            onClick={saveCloseoutDraft}
            disabled={saveMut.running}
          >
            {saveMut.running ? 'Saving…' : 'Save closeout draft'}
          </button>
          {readiness?.canApprove &&
          closeoutStatus !== 'approved' &&
          closeoutStatus !== 'pending_approval' ? (
            <button
              type="button"
              className="btn outline"
              onClick={() => setPendingCloseoutAction('route')}
              disabled={approveMut.running}
            >
              {approveMut.running ? 'Working…' : 'Route for approval'}
            </button>
          ) : null}
          {readiness?.canApprove && closeoutStatus === 'pending_approval' ? (
            <button
              type="button"
              className="btn outline"
              onClick={() => setPendingCloseoutAction('approve')}
              disabled={approveMut.running}
            >
              {approveMut.running ? 'Working…' : 'Approve closeout package'}
            </button>
          ) : null}
          {closeoutStatus === 'approved' && !projectArchived ? (
            <button
              type="button"
              className="btn ghost"
              onClick={() => setPendingCloseoutAction('archive')}
              disabled={archiveMut.running}
            >
              {archiveMut.running ? 'Archiving…' : 'Archive project'}
            </button>
          ) : null}
        </div>
      </section>
      <ConfirmActionDialog
        open={pendingCloseoutAction === 'route'}
        title="Route closeout package for approval?"
        body="Routing moves this package into pending approval for an explicit approval decision. It does not approve the package."
        confirmLabel="Route for approval"
        busy={approveMut.running}
        onCancel={() => setPendingCloseoutAction(null)}
        onConfirm={async () => {
          setPendingCloseoutAction(null);
          await routeForApproval();
        }}
      />
      <ConfirmActionDialog
        open={pendingCloseoutAction === 'approve'}
        title="Approve closeout package?"
        body="Approving certifies the required closeout records are complete and unlocks project archival. The decision is audited."
        confirmLabel="Approve package"
        busy={approveMut.running}
        onCancel={() => setPendingCloseoutAction(null)}
        onConfirm={async () => {
          setPendingCloseoutAction(null);
          await approveCloseoutPackage();
        }}
      />
      <ConfirmActionDialog
        open={pendingCloseoutAction === 'archive'}
        title={`Archive project ${id}?`}
        body="Archiving closes out the project record with the approved closeout package attached. This is an outward-facing, audited change."
        confirmLabel="Archive project"
        tone="danger"
        busy={archiveMut.running}
        onCancel={() => setPendingCloseoutAction(null)}
        onConfirm={async () => {
          setPendingCloseoutAction(null);
          await archiveProject();
        }}
      />
    </>
  );
}
